import { FC } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslate } from 'hooks/useTranslate';
import { format } from 'date-fns';
import makeAnimated from 'react-select/animated';
import Select from 'react-select';

import actionTypes from 'constants/actionTypes';

import MandatoryField from 'modules/_shared/components/MandatoryField';
import InputTextArea from 'modules/_shared/components/InputTextArea';
import InputText from 'modules/_shared/components/InputText';
import AddBoardsDocumentSection from 'modules/_shared/components/AddBoardsDocumentsSection';

import { useAddBasicBoardModal } from '../context/AddBasicBoardModalContext';

const attendanceTypes = {
  HYBRID: 'HYBRID',
  ONLINE: 'ONLINE',
  FACE_TO_FACE: 'FACE_TO_FACE',
  WRITTEN_NO_SESSION: 'WRITTEN_NO_SESSION',
};

const AddBasicBoardModalBody: FC = () => {
  const { t } = useTranslate();
  const animatedComponents = makeAnimated();

  const { data, actions } = useAddBasicBoardModal();

  const placeInputDisabled =
    data.selectedAttendanceType === attendanceTypes.ONLINE ||
    data.selectedAttendanceType === attendanceTypes.WRITTEN_NO_SESSION;

  const onlineAccessDisabled =
    data.selectedAttendanceType === attendanceTypes.FACE_TO_FACE ||
    data.selectedAttendanceType === attendanceTypes.WRITTEN_NO_SESSION;

  return (
    <Modal.Body>
      <div className="nk-block form-contact g-4">
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <InputText
                id="name"
                label={t('BoardName')}
                value={data.name}
                onChange={actions.handleChangeName}
                required
                lgSize
              />
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="nk-block-head-content col-12">
            <h5 className="nk-block-title mb-0">{t('BasicInfo')}</h5>
          </div>

          <div className="col-3">
            <div className="form-group">
              <label className="form-label" htmlFor="date">
                {t('DateAndTime')} <MandatoryField />
              </label>
              <div className="form-control-wrap">
                <input
                  id="date"
                  type="datetime-local"
                  className="form-control form-control-lg date-picker"
                  value={format(new Date(data.date), "yyyy-MM-dd'T'HH:mm")}
                  placeholder="dd/mm/yyyy"
                  onChange={actions.handleChangeDate}
                  required
                />
              </div>
            </div>
          </div>

          <div className="col-3">
            <div className="form-group">
              <label className="form-label" htmlFor="attendance">
                {t('FormOfAssistance')} <MandatoryField />
              </label>
              <div>
                <Select
                  id="attendance"
                  closeMenuOnSelect
                  value={data.selectedAttendanceTypeOption}
                  className="react-select react-select-lg"
                  options={data.attendanceTypeOptions}
                  components={animatedComponents}
                  onChange={actions.handleChangeAttendanceType}
                  isMulti={false}
                />
              </div>
            </div>
          </div>

          <div className="col-3">
            <div className="form-group">
              <label className="form-label" htmlFor="status">
                {t('Status')} <MandatoryField />
              </label>
              <div>
                <Select
                  id="status"
                  closeMenuOnSelect
                  value={data.selectedStatusOption}
                  className="react-select react-select-lg"
                  options={data.statusOptions}
                  components={animatedComponents}
                  onChange={actions.handleChangeStatus}
                  isMulti={false}
                />
              </div>
            </div>
          </div>

          <div className="col-3">
            <div className="form-group">
              <label className="form-label" htmlFor="isVisible">
                {t('Visibility')} <MandatoryField />
              </label>
              <div>
                <Select
                  id="isVisible"
                  closeMenuOnSelect
                  value={data.selectedVisibilityOption}
                  className="react-select react-select-lg"
                  options={data.visibilityOptions}
                  components={animatedComponents}
                  onChange={actions.handleChangeVisibility}
                  isMulti={false}
                />
              </div>
            </div>
          </div>

          <div className="col-6 ">
            <div className="form-group">
              <div className="form-control-wrap">
                <InputText
                  id="place"
                  label={t('PlaceOfCelebration')}
                  placeholder={t('PlaceOfCelebration')}
                  value={data.place}
                  onChange={actions.handleChangePlace}
                  disabled={placeInputDisabled}
                  required={!placeInputDisabled}
                  lgSize
                />
              </div>
            </div>
          </div>

          <div className="col-6">
            <div className="form-group">
              <InputText
                id="onlineAccess"
                label={t('OnlineAccessOptional')}
                placeholder={t('AccessLink')}
                value={data.onlineAccess}
                onChange={actions.handleChangeOnlineAccess}
                disabled={onlineAccessDisabled}
                lgSize
              />
            </div>
          </div>

          <div className="col-12">
            <div className="form-group">
              <InputTextArea
                id="comments"
                label={t('CommentsLabel')}
                value={data.comments}
                onChange={actions.handleChangeComments}
                maxLength={3000}
                rows={3}
                noResize
                lgSize
              />
            </div>
          </div>

          {data.actionType === actionTypes.ADD && (
            <AddBoardsDocumentSection
              files={data.files}
              onChangeFiles={actions.handleChangeFiles}
              onRemoveFile={actions.handleRemoveFile}
            />
          )}
        </div>
      </div>
    </Modal.Body>
  );
};

export default AddBasicBoardModalBody;
